import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import {
    ProductCategorySearchBuilder,
    ProductCategorySearchResponse,
    ProductSearchBuilder,
    ProductSearchResponse,
    SearchCollectionBuilder,
    Searcher
} from '@relewise/client';
import { ProductData, RelewiseCategoryResult, RelewiseProductResult } from '../types/relewiseData';
import { CategoryProductResult, CategoryProductsResponse } from '../types/mapped';
import {
    NUMBER_OF_VARIANTS_PER_PRODUCT_IN_PLP,
    DEFAULT_PAGE_SIZE,
    DISTINCT_COLOR_VARIANT_COUNT,
    RELEWISE_DISPLAYED_AT_LOCATION
} from '../const';
import { RelewiseMappingService } from './MappingService';
import { FilterOptions } from '../../../utils/productListFilters';
import { ProductListSortOption } from '../../../utils/productListSort';
import { InitializedRelewiseService, BaseRelewiseService } from './BaseRelewiseService/BaseRelewiseService';

interface InitializedService extends InitializedRelewiseService {
    relewiseMappingService: RelewiseMappingService;
    searcher: Searcher;
}

export class RelewiseCategoryService extends BaseRelewiseService<InitializedService> {
    protected searcher: Searcher | undefined;
    protected relewiseMappingService: RelewiseMappingService | undefined;

    constructor(request: IRequestContext, telemetry: ITelemetry) {
        super(request, telemetry, 'Category Service');

        if (!this.isEnabled()) {
            return;
        }

        this.relewiseMappingService = new RelewiseMappingService(request, telemetry);
        this.searcher = new Searcher(this.relewiseDatasetId, this.relewiseApiKey, {
            serverUrl: this.relewiseUrl
        });
    }

    public async getCategoryProducts(
        categoryRecordId: string,
        itemsPerPage: number = DEFAULT_PAGE_SIZE,
        page: number = 1,
        sort: ProductListSortOption = 'recommended',
        variantsPerProduct: number = NUMBER_OF_VARIANTS_PER_PRODUCT_IN_PLP,
        filterOptions?: FilterOptions
    ): Promise<CategoryProductsResponse | undefined> {
        if (!this.isEnabled()) {
            return;
        }
        try {
            if (!categoryRecordId) {
                throw new Error(`Missing categoryRecordId, environment=${this.environmentName}`);
            }

            const categoryRelewiseId = this.relewiseMappingService.prefixId(categoryRecordId);

            const settings = await this.getApiRequestSettings(RELEWISE_DISPLAYED_AT_LOCATION.CATEGORY_PLP);

            const categoryBuilder = new ProductCategorySearchBuilder(settings)
                .setSelectedCategoryProperties({ displayName: true, dataKeys: ['MetaTitle', 'MetaDescription'] })
                .filters(f => {
                    this.addCategoryEnvironmentFilter(f);
                    f.addProductCategoryDataFilter(
                        'RecordId',
                        b => b.addEqualsCondition({ value: categoryRecordId, type: 'Double', isCollection: false }),
                        true,
                        true
                    );
                })
                .facets(f => f.addProductCategoryHierarchyFacet('Descendants', undefined, { displayName: true }));

            const productBuilder = new ProductSearchBuilder(settings)
                .pagination(p => p.setPageSize(itemsPerPage).setPage(page))
                .setVariantRequestSettings(variantSettings => {
                    variantSettings.setMaxVariantsPerProduct(variantsPerProduct);
                })
                .searchConstraints(searchConstraints => {
                    searchConstraints.setResultMustHaveVariantConstraint({
                        exceptWhenProductHasNoVariants: false
                    });
                })
                .setSelectedProductProperties(this.getSelectedProductPropertiesForSimpleProduct())
                .setSelectedVariantProperties(this.getSelectedVariantPropertiesForSimpleProduct())
                .facets(f => {
                    f.addSalesPriceRangeFacet('Product', filterOptions?.priceRange?.min, filterOptions?.priceRange?.max);
                    f.addCategoryFacet('ImmediateParent', filterOptions?.categoryIds);
                    f.addProductDataStringValueFacet('Size', 'Variant', filterOptions?.sizes);
                    f.addProductDataStringValueFacet('ColorRefinerGroup', 'Variant', filterOptions?.colors);
                })
                .filters(f => {
                    f.addProductCategoryIdFilter('Ancestor', categoryRelewiseId);
                    this.addProductEnvironmentFilter(f);
                    this.addProductAndVariantStockFilters(f);
                    f.addVariantDataFilter('UniqueColorId', b => b.addDistinctCondition(DISTINCT_COLOR_VARIANT_COUNT), true, true);
                });

            if (sort === 'price-asc') {
                productBuilder.sorting(s =>
                    s.sortByProductVariantAttribute('SalesPrice', 'Ascending', thenBy => thenBy.sortByProductRelevance('Descending'))
                );
            } else if (sort === 'price-desc') {
                productBuilder.sorting(s =>
                    s.sortByProductVariantAttribute('SalesPrice', 'Descending', thenBy => thenBy.sortByProductRelevance('Descending'))
                );
            }

            const batchedRequestBuilder = new SearchCollectionBuilder();
            batchedRequestBuilder.addRequest(categoryBuilder.build());
            batchedRequestBuilder.addRequest(productBuilder.build());

            const batchResponse = await this.searcher.batch(batchedRequestBuilder.build());
            const productResponse = batchResponse?.responses?.pop() as ProductSearchResponse | undefined;
            const categoryResponse = batchResponse?.responses?.pop() as ProductCategorySearchResponse | undefined;

            const category = categoryResponse?.results?.[0] as RelewiseCategoryResult;
            if (!category?.categoryId) {
                throw new Error(`Category not found in dataset for recordId=${categoryRecordId} environment=${this.environmentName}`);
            }

            if (!productResponse?.results) {
                return {
                    categoryDisplayName: category.displayName ?? undefined,
                    relewiseCategoryId: category.categoryId,
                    productList: [],
                    totalItems: 0,
                    currentPage: page,
                    itemsPerPage,
                    totalPages: 0,
                    seoText: undefined,
                    seoTitle: undefined
                };
            }

            const productList = productResponse.results
                .filter(product => product.variant !== null)
                .map<CategoryProductResult>(product => ({
                    variant: this.relewiseMappingService?.mapRelewiseProductResult(product as RelewiseProductResult),
                    colors: this.relewiseMappingService?.mapProductColors(product.data as ProductData) || []
                }));

            const totalItems = productResponse.hits ?? 0;
            const totalPages = itemsPerPage > 0 ? Math.ceil(totalItems / itemsPerPage) : 0;
            const result: CategoryProductsResponse = {
                categoryDisplayName: category.displayName ?? undefined,
                relewiseCategoryId: category.categoryId,
                productList,
                totalItems,
                currentPage: page,
                itemsPerPage,
                totalPages,
                seoText: category.data?.MetaDescription?.value ?? undefined,
                seoTitle: category.data?.MetaTitle?.value ?? undefined
            };

            return result;
        } catch (err) {
            this.logError(`getCategoryProducts request failed: ${err instanceof Error ? err.message : String(err)}`);
            return;
        }
    }
}
